import { useInfiniteQuery, useQueries, useQuery } from '@tanstack/react-query';
import {
  getListMeta, getUnreadCounts, listConversations, listInboxes, listInboxStages,
} from '@/api/inbox';
import type { CwInboxFull, CwInboxStage } from '@/api/inboxTypes';
import { PAGE_SIZE, badgeQueries, type ListQuery } from '@/lib/inboxQuery';
import { cwKeys } from '@/lib/cwCache';
import { countUnread, flatten, hasNextPage, type ListData, type ListPage } from '@/lib/convList';
import { useCwSession } from './useCwSession';

/** Список переписок одной выборки: страницы по 25, догрузка при прокрутке. */
export function useConversationList(accountId: number, query: ListQuery, enabled: boolean) {
  return useInfiniteQuery<ListPage, Error, ListData, ReturnType<typeof cwKeys.convs>, number>({
    queryKey: cwKeys.convs(accountId, query),
    enabled,
    initialPageParam: 1,
    queryFn: ({ pageParam }) => listConversations(accountId, query, pageParam),
    getNextPageParam: (last, all) => {
      const data: ListData = { pages: all, pageParams: [] };
      return last.conversations.length >= PAGE_SIZE && hasNextPage(data) ? all.length + 1 : undefined;
    },
    // Живые события держат кэш свежим; полный перезапрос — только при обрыве связи/возврате из фона.
    staleTime: 5 * 60_000,
  });
}

/** Сколько переписок в выборке (подзаголовок «N ждут ответа»). */
export function useListMeta(accountId: number, query: ListQuery, enabled: boolean) {
  return useQuery({
    queryKey: cwKeys.meta(accountId, query),
    enabled,
    queryFn: () => getListMeta(accountId, query),
    staleTime: 30_000,
  });
}

export function useInboxes(accountId: number, enabled: boolean) {
  return useQuery<CwInboxFull[]>({
    queryKey: cwKeys.inboxes(accountId),
    enabled,
    queryFn: () => listInboxes(accountId),
    staleTime: 10 * 60_000,
  });
}

/** Стадии всех каналов (нужны и для цвета точки у строки, и для полосы воронки). */
export function useAllStages(accountId: number, inboxes: CwInboxFull[] | undefined, enabled: boolean) {
  const list = inboxes ?? [];
  const results = useQueries({
    queries: list.map((i) => ({
      queryKey: cwKeys.stages(accountId, i.id),
      enabled,
      queryFn: () => listInboxStages(accountId, i.id),
      staleTime: 10 * 60_000,
    })),
  });
  const byInbox = new Map<number, CwInboxStage[]>();
  list.forEach((i, n) => {
    const d = results[n]?.data;
    if (d) byInbox.set(i.id, [...d].sort((a, b) => a.position - b.position));
  });
  return { byInbox, loading: results.some((r) => r.isPending && enabled) };
}

/** Число непрочитанных по каналам и стадиям (null — на кабинете функция выключена). */
export function useUnreadCounts(accountId: number, enabled: boolean) {
  return useQuery({
    queryKey: cwKeys.unread(accountId),
    enabled,
    queryFn: () => getUnreadCounts(accountId),
    staleTime: 60_000,
  });
}

/**
 * Значок вкладки «Входящие» = число переписок с непрочитанным, которые ведёт ЧЕЛОВЕК или никто:
 * назначенные на меня + свободные (open). Чужие (у коллег) и «у бота» не считаются — они не от меня ждут ответа.
 * Берём из двух лёгких запросов (первая страница, непрочитанные вперёд), поэтому предел точного
 * счёта — 25 на каждый; события реального времени правят их на месте.
 * Серверный unread_counts не годится: в режиме администратора он считает ВСЕ чужие переписки.
 */
export function useTabBadge(): number {
  const { accountId, enabled } = useCwSession();
  const [mine, free] = badgeQueries();
  const acc = accountId ?? 0;
  const a = useConversationList(acc, mine, enabled);
  const b = useConversationList(acc, free, enabled);
  if (!enabled) return 0;
  return countUnread([a.data, b.data]);
}

export { flatten };
